import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'sonner';
import {
  Search, Plus, Users, Building, Mail, Phone, Globe, MapPin, 
  Target, TrendingUp, Calendar, CheckCircle, X, MoreVertical,
  Clock, Tag, Send, Trash2, Edit, DollarSign, FileText, 
  MessageSquare, Activity, RefreshCw, ChevronDown, ChevronRight,
  Briefcase, Zap, Eye, Upload, Download, Filter, BarChart3,
  ArrowRight, Sparkles, ExternalLink, Loader2, Copy, Check,
  AlertCircle, GripVertical, Lock
} from 'lucide-react';

const API_URL = window.location.origin;

// Pipeline stage configuration
const PIPELINE_STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-500', textColor: 'text-blue-400' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  { id: 'replied', label: 'Replied', color: 'bg-green-500', textColor: 'text-green-400' },
  { id: 'qualified', label: 'Qualified', color: 'bg-cyan-500', textColor: 'text-cyan-400' },
  { id: 'call_scheduled', label: 'Call Scheduled', color: 'bg-purple-500', textColor: 'text-purple-400' },
  { id: 'proposal_sent', label: 'Proposal Sent', color: 'bg-pink-500', textColor: 'text-pink-400' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500', textColor: 'text-orange-400' },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-emerald-500', textColor: 'text-emerald-400' },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500', textColor: 'text-red-400' },
];

const INDUSTRIES = [
  'All', 'Technology', 'Healthcare', 'Finance', 'Real Estate', 'Retail', 
  'Manufacturing', 'Education', 'Marketing', 'Legal', 'Consulting', 'Entertainment', 'Other'
];

const COMPANY_SIZES = [
  { value: 'All', label: 'All Sizes' },
  { value: '1-10', label: '1-10 employees' },
  { value: '10-50', label: '10-50 employees' },
  { value: '50-200', label: '50-200 employees' },
  { value: '200+', label: '200+ employees' },
];

const PRIORITIES = ['A', 'B', 'C'];

// ============== COMPONENTS ==============

const WeeklyMissionPanel = ({ mission }) => {
  return (
    <div className="bg-black border border-gray-700 rounded-xl p-6 mb-6">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Target className="text-cyan-400" size={24} />
        Weekly Mission
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="text-3xl font-bold text-cyan-400">{mission.new_leads_this_week}</div>
          <div className="text-xs text-gray-400 mt-1">New Leads This Week</div>
          {mission.leads_needed > 0 && (
            <div className="text-xs text-yellow-400 mt-1">Need {mission.leads_needed} more</div>
          )}
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="text-3xl font-bold text-blue-400">{mission.emails_scheduled}</div>
          <div className="text-xs text-gray-400 mt-1">Emails Scheduled</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="text-3xl font-bold text-green-400">{mission.replies}</div>
          <div className="text-xs text-gray-400 mt-1">Replies</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="text-3xl font-bold text-purple-400">{mission.calls_booked}</div>
          <div className="text-xs text-gray-400 mt-1">Calls Booked</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="text-3xl font-bold text-emerald-400">
            ${(mission.pipeline_value || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Pipeline Value</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
          <div className="text-3xl font-bold text-yellow-400">
            ${(mission.won_value || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">Won Revenue</div>
        </div>
      </div>
    </div>
  );
};

const TodaysActionsPanel = ({ actions, onRefresh }) => {
  return (
    <div className="bg-black border border-gray-700 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Zap className="text-yellow-400" size={24} />
          Today's Actions
        </h2>
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Follow-ups Due */}
        <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="text-red-400" size={18} />
            <span className="text-red-400 font-medium">Follow-ups Due</span>
            <span className="ml-auto bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full text-xs">
              {actions.followups_due}
            </span>
          </div>
          {actions.followup_leads?.length > 0 ? (
            <div className="space-y-2">
              {actions.followup_leads.slice(0, 3).map((lead, idx) => (
                <div key={idx} className="text-sm text-gray-300 truncate">
                  {lead.company?.name || 'Unknown Company'}
                </div>
              ))}
              {actions.followups_due > 3 && (
                <div className="text-xs text-gray-500">+{actions.followups_due - 3} more</div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No follow-ups due today</div>
          )}
        </div>

        {/* Leads to Research */}
        <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Search className="text-blue-400" size={18} />
            <span className="text-blue-400 font-medium">Needs Research</span>
            <span className="ml-auto bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full text-xs">
              {actions.leads_to_research}
            </span>
          </div>
          {actions.research_leads?.length > 0 ? (
            <div className="space-y-2">
              {actions.research_leads.slice(0, 3).map((lead, idx) => (
                <div key={idx} className="text-sm text-gray-300 truncate">
                  {lead.company?.name || 'Unknown Company'}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">All leads researched</div>
          )}
        </div>

        {/* Drafts Pending */}
        <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="text-purple-400" size={18} />
            <span className="text-purple-400 font-medium">Drafts Pending</span>
            <span className="ml-auto bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full text-xs">
              {actions.drafts_pending}
            </span>
          </div>
          {actions.pending_drafts?.length > 0 ? (
            <div className="space-y-2">
              {actions.pending_drafts.slice(0, 3).map((item, idx) => (
                <div key={idx} className="text-sm text-gray-300 truncate">
                  {item.company?.name || 'Unknown'}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No pending drafts</div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetricsPanel = ({ metrics }) => {
  return (
    <div className="bg-black border border-gray-700 rounded-xl p-6 mb-6">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <BarChart3 className="text-green-400" size={24} />
        Performance Metrics
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-white">{metrics.total_leads}</div>
          <div className="text-xs text-gray-400">Total Leads</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-blue-400">{metrics.contacted}</div>
          <div className="text-xs text-gray-400">Contacted</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-green-400">{metrics.replied}</div>
          <div className="text-xs text-gray-400">Replied</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-purple-400">{metrics.reply_rate}%</div>
          <div className="text-xs text-gray-400">Reply Rate</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-emerald-400">{metrics.conversion_rate}%</div>
          <div className="text-xs text-gray-400">Conversion</div>
        </div>
      </div>
    </div>
  );
};

// Company Modal
const CompanyModal = ({ isOpen, onClose, company, onSave, onDelete, mode = 'create' }) => {
  const [form, setForm] = useState({
    name: '',
    website: '',
    industry: '',
    location: '',
    size: '',
    notes: '',
    lead_source: '',
    priority: 'B',
    budget_signals: []
  });
  const [isEnriching, setIsEnriching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (company && mode === 'edit') {
      setForm({
        name: company.name || company.company_name || '',
        website: company.website || '',
        industry: company.industry || '',
        location: company.location || '',
        size: company.size || company.company_size || '',
        notes: company.notes || '',
        lead_source: company.lead_source || '',
        priority: company.priority || 'B',
        budget_signals: company.budget_signals || []
      });
    } else {
      setForm({
        name: '',
        website: '',
        industry: '',
        location: '',
        size: '',
        notes: '',
        lead_source: '',
        priority: 'B',
        budget_signals: []
      });
    }
  }, [company, mode]);

  const handleEnrich = async () => {
    if (!company?.id) return;
    setIsEnriching(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/${company.id}/enrich`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Company enriched with website data!');
        if (data.scraped_data) {
          setForm(prev => ({
            ...prev,
            notes: `${prev.notes}\n\n--- Scraped Data ---\nHeadline: ${data.scraped_data.headline || 'N/A'}\nDescription: ${data.scraped_data.description || 'N/A'}`
          }));
        }
      } else {
        toast.error(data.detail || 'Failed to enrich company');
      }
    } catch (error) {
      toast.error('Failed to enrich company');
    } finally {
      setIsEnriching(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setIsSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {mode === 'create' ? 'Add New Company' : 'Edit Company'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Website</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="flex-1 px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="www.acme.com"
                />
                {mode === 'edit' && form.website && (
                  <button
                    onClick={handleEnrich}
                    disabled={isEnriching}
                    className="px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 disabled:opacity-50"
                    title="Enrich with website data"
                  >
                    {isEnriching ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Industry</label>
              <select
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                <option value="">Select Industry</option>
                {INDUSTRIES.filter(i => i !== 'All').map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Location</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="Dallas, TX"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Size</label>
              <select
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                <option value="">Select Size</option>
                {COMPANY_SIZES.filter(s => s.value !== 'All').map(size => (
                  <option key={size.value} value={size.value}>{size.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              >
                {PRIORITIES.map(p => (
                  <option key={p} value={p}>Priority {p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Lead Source</label>
              <input
                type="text"
                value={form.lead_source}
                onChange={(e) => setForm({ ...form, lead_source: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="LinkedIn, Referral, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none"
              rows={4}
              placeholder="Any notes about this company..."
            />
          </div>

          {company?.scraped_data && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <h4 className="text-cyan-400 font-medium mb-2 flex items-center gap-2">
                <Sparkles size={16} />
                Enriched Data
              </h4>
              <div className="text-sm text-gray-300 space-y-1">
                {company.scraped_data.headline && (
                  <p><span className="text-gray-500">Headline:</span> {company.scraped_data.headline}</p>
                )}
                {company.scraped_data.description && (
                  <p><span className="text-gray-500">Description:</span> {company.scraped_data.description}</p>
                )}
                {company.scraped_data.hiring_signals?.length > 0 && (
                  <p><span className="text-gray-500">Signals:</span> {company.scraped_data.hiring_signals.join(', ')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          {mode === 'edit' && (
            <button
              onClick={() => onDelete(company.id)}
              className="px-4 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
            >
              Delete Company
            </button>
          )}
          <div className={`flex gap-3 ${mode === 'create' ? 'ml-auto' : ''}`}>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : mode === 'create' ? 'Add Company' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Email Draft Modal
const EmailDraftModal = ({ isOpen, onClose, lead, company, onApprove }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(0);
  const [selectedVersion, setSelectedVersion] = useState('short');
  const [editedContent, setEditedContent] = useState('');
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [copiedBody, setCopiedBody] = useState(false);

  const generateDraft = async () => {
    if (!lead || !company) return;
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: lead.id,
          company_data: company,
          contact_name: lead.contact?.name || 'there'
        })
      });
      const data = await response.json();
      if (response.ok) {
        setDraft(data.draft);
        setEditedContent(data.draft.short_version);
        toast.success('Email draft generated!');
      } else {
        toast.error(data.detail || 'Failed to generate draft');
      }
    } catch (error) {
      toast.error('Failed to generate draft');
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (draft) {
      setEditedContent(selectedVersion === 'short' ? draft.short_version : draft.long_version);
    }
  }, [selectedVersion, draft]);

  const copyToClipboard = async (text, type) => {
    await navigator.clipboard.writeText(text);
    if (type === 'subject') {
      setCopiedSubject(true);
      setTimeout(() => setCopiedSubject(false), 2000);
    } else {
      setCopiedBody(true);
      setTimeout(() => setCopiedBody(false), 2000);
    }
    toast.success('Copied to clipboard!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="text-purple-400" size={24} />
              AI Email Generator
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Generate personalized email for {company?.name}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {!draft ? (
            <div className="text-center py-12">
              <Sparkles className="mx-auto text-purple-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-white mb-2">
                Ready to generate personalized email
              </h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                AI will analyze {company?.name}'s website and create a customized outreach email with multiple options.
              </p>
              <button
                onClick={generateDraft}
                disabled={isGenerating}
                className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2 mx-auto"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    Generate Email Draft
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Subject Lines */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Subject Line Options</label>
                <div className="space-y-2">
                  {draft.subject_options?.map((subject, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedSubject(idx)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedSubject === idx
                          ? 'bg-purple-500/20 border border-purple-500'
                          : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedSubject === idx ? 'border-purple-500' : 'border-gray-600'
                      }`}>
                        {selectedSubject === idx && <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />}
                      </div>
                      <span className="text-white flex-1">{subject}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(subject, 'subject'); }}
                        className="p-1 hover:bg-gray-700 rounded"
                      >
                        {copiedSubject && selectedSubject === idx ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Version Toggle */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email Version</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedVersion('short')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedVersion === 'short'
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Short Version
                  </button>
                  <button
                    onClick={() => setSelectedVersion('long')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedVersion === 'long'
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Long Version
                  </button>
                </div>
              </div>

              {/* Email Body */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-400">Email Body</label>
                  <button
                    onClick={() => copyToClipboard(editedContent, 'body')}
                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
                  >
                    {copiedBody ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                    Copy
                  </button>
                </div>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none resize-none font-mono text-sm"
                  rows={12}
                />
              </div>

              {/* Personalized Line Preview */}
              {draft.personalized_line && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="text-yellow-400 text-sm font-medium mb-1">Personalized Opening</h4>
                  <p className="text-gray-300">{draft.personalized_line}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {draft && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
            <button
              onClick={generateDraft}
              disabled={isGenerating}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Regenerate
            </button>
            <button
              onClick={() => {
                onApprove({
                  subject: draft.subject_options[selectedSubject],
                  body: editedContent
                });
                onClose();
              }}
              className="px-6 py-2 bg-green-500 text-black font-medium rounded-lg hover:bg-green-400 flex items-center gap-2"
            >
              <Check size={18} />
              Approve & Queue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Lead Detail Modal - Shows full lead and contact information
const LeadDetailModal = ({ isOpen, onClose, lead, onEdit, onGenerateEmail }) => {
  if (!isOpen || !lead) return null;
  
  const companyName = lead.company?.name || lead.company?.company_name || 'Unknown Company';
  const contact = lead.contact;
  const stageConfig = PIPELINE_STAGES.find(s => s.id === lead.stage) || PIPELINE_STAGES[0];
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
              {companyName.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{companyName}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded ${stageConfig.color} text-white`}>
                  {stageConfig.label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  lead.company?.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                  lead.company?.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  Priority {lead.company?.priority || 'B'}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-lead-modal">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Company Info */}
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <Building size={16} />
              Company Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {lead.company?.website && (
                <div>
                  <div className="text-xs text-gray-500">Website</div>
                  <a 
                    href={lead.company.website.startsWith('http') ? lead.company.website : `https://${lead.company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  >
                    <Globe size={12} />
                    {lead.company.website}
                  </a>
                </div>
              )}
              {lead.company?.industry && (
                <div>
                  <div className="text-xs text-gray-500">Industry</div>
                  <div className="text-sm text-white">{lead.company.industry}</div>
                </div>
              )}
              {lead.company?.location && (
                <div>
                  <div className="text-xs text-gray-500">Location</div>
                  <div className="text-sm text-white flex items-center gap-1">
                    <MapPin size={12} />
                    {lead.company.location}
                  </div>
                </div>
              )}
              {(lead.company?.size || lead.company?.company_size) && (
                <div>
                  <div className="text-xs text-gray-500">Company Size</div>
                  <div className="text-sm text-white">{lead.company.size || lead.company.company_size}</div>
                </div>
              )}
              {lead.company?.lead_source && (
                <div>
                  <div className="text-xs text-gray-500">Lead Source</div>
                  <div className="text-sm text-white">{lead.company.lead_source}</div>
                </div>
              )}
              {lead.deal_value > 0 && (
                <div>
                  <div className="text-xs text-gray-500">Deal Value</div>
                  <div className="text-sm text-green-400 font-medium">${lead.deal_value.toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          {contact && (
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-lg p-4">
              <h3 className="text-sm font-medium text-cyan-400 mb-3 flex items-center gap-2">
                <Users size={16} />
                Contact Information
              </h3>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {contact.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <div className="text-lg font-medium text-white">{contact.name}</div>
                    {contact.title && (
                      <div className="text-sm text-gray-400">{contact.title}</div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {contact.email && (
                      <a 
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"
                      >
                        <Mail size={14} />
                        {contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <a 
                        href={`tel:${contact.phone}`}
                        className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
                      >
                        <Phone size={14} />
                        {contact.phone}
                      </a>
                    )}
                    {contact.linkedin_url && (
                      <a 
                        href={contact.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                      >
                        <ExternalLink size={14} />
                        LinkedIn Profile
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Contact Message */}
          {!contact && (
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <Users size={32} className="mx-auto text-gray-600 mb-2" />
              <p className="text-gray-400">No contact information available</p>
              <p className="text-xs text-gray-500 mt-1">Add a contact to this lead to see details here</p>
            </div>
          )}

          {/* Lead Notes */}
          {lead.notes && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                <FileText size={16} />
                Notes
              </h3>
              <p className="text-sm text-gray-300">{lead.notes}</p>
            </div>
          )}

          {/* Timeline Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {lead.created_at && (
              <div className="text-gray-500">
                <span className="text-gray-600">Created:</span> {new Date(lead.created_at).toLocaleDateString()}
              </div>
            )}
            {lead.next_followup_date && (
              <div className="text-yellow-400">
                <Clock size={12} className="inline mr-1" />
                Follow-up: {new Date(lead.next_followup_date).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={() => { onClose(); onEdit(lead); }}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <Edit size={16} />
            Edit Lead
          </button>
          {contact?.email && (
            <button
              onClick={() => { onClose(); onGenerateEmail(lead); }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 flex items-center gap-2"
            >
              <Mail size={16} />
              Generate Email
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Lead Card for Pipeline
const LeadCard = ({ lead, onEdit, onGenerateEmail, onDelete, onViewDetails, provided }) => {
  const stageConfig = PIPELINE_STAGES.find(s => s.id === lead.stage) || PIPELINE_STAGES[0];
  const companyName = lead.company?.name || lead.company?.company_name || 'Unknown Company';
  const contact = lead.contact;
  
  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onClick={() => onViewDetails(lead)}
      className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-3 hover:border-gray-600 transition-all cursor-pointer group"
      data-testid="pipeline-lead-card"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <GripVertical size={14} className="text-gray-600 opacity-0 group-hover:opacity-100" />
          <div className={`w-2 h-2 rounded-full ${stageConfig.color}`} />
          <span className="font-medium text-white truncate max-w-[150px]">
            {companyName}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {lead.deal_value > 0 && (
            <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">
              ${(lead.deal_value / 1000).toFixed(0)}k
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded ${
            lead.company?.priority === 'A' ? 'bg-red-500/20 text-red-400' :
            lead.company?.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-gray-500/20 text-gray-400'
          }`}>
            {lead.company?.priority || 'B'}
          </span>
        </div>
      </div>

      {/* Contact Info Section */}
      {contact && (
        <div className="mb-2 space-y-1">
          <div className="text-sm text-white flex items-center gap-1">
            <Users size={12} className="text-cyan-400" />
            {contact.name}
            {contact.title && <span className="text-gray-500">• {contact.title}</span>}
          </div>
          {contact.email && (
            <div className="text-xs text-cyan-400 flex items-center gap-1">
              <Mail size={10} />
              {contact.email}
            </div>
          )}
          {contact.phone && (
            <div className="text-xs text-gray-400 flex items-center gap-1">
              <Phone size={10} />
              {contact.phone}
            </div>
          )}
        </div>
      )}

      {lead.company?.industry && (
        <div className="text-xs text-gray-500 mb-2">{lead.company.industry}</div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(lead); }}
            className="p-1.5 hover:bg-cyan-500/20 rounded text-gray-400 hover:text-cyan-400 transition-colors"
            title="Edit lead"
          >
            <Edit size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onGenerateEmail(lead); }}
            className="p-1.5 hover:bg-purple-500/20 rounded text-gray-400 hover:text-purple-400 transition-colors"
            title="Generate email"
          >
            <Mail size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
            className="p-1.5 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400 transition-colors"
            title="Delete lead"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {lead.next_followup_date && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Clock size={12} />
            {new Date(lead.next_followup_date).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

// Pipeline Board
const PipelineBoard = ({ pipeline, onDragEnd, onEditLead, onGenerateEmail, onDeleteLead, onViewLeadDetails }) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map(stage => (
          <div key={stage.id} className="flex-shrink-0 w-72">
            <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
              <div className={`px-4 py-3 border-b border-gray-700 flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                  <span className="font-medium text-white">{stage.label}</span>
                </div>
                <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                  {pipeline[stage.id]?.length || 0}
                </span>
              </div>
              <Droppable droppableId={stage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`p-3 min-h-[300px] max-h-[60vh] overflow-y-auto ${
                      snapshot.isDraggingOver ? 'bg-gray-900/50' : ''
                    }`}
                  >
                    {pipeline[stage.id]?.map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided) => (
                          <LeadCard
                            lead={lead}
                            onEdit={onEditLead}
                            onGenerateEmail={onGenerateEmail}
                            onDelete={onDeleteLead}
                            onViewDetails={onViewLeadDetails}
                            provided={provided}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {(!pipeline[stage.id] || pipeline[stage.id].length === 0) && (
                      <div className="text-center py-8 text-gray-600 text-sm">
                        No leads
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        ))}
      </div>
    </DragDropContext>
  );
};

// Companies List
const CompaniesTable = ({ companies, onEdit, onCreateLead, onEnrich, onBulkDelete }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(companies.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedIds, id];
      setSelectedIds(newSelected);
      if (newSelected.length === companies.length) {
        setSelectAll(true);
      }
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`Delete ${selectedIds.length} selected companies and all their related data?`)) {
      onBulkDelete(selectedIds);
      setSelectedIds([]);
      setSelectAll(false);
    }
  };

  return (
    <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="px-4 py-3 bg-red-500/10 border-b border-red-500/30 flex items-center justify-between">
          <span className="text-red-400 font-medium">
            {selectedIds.length} {selectedIds.length === 1 ? 'company' : 'companies'} selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 font-medium transition-colors"
          >
            <Trash2 size={16} />
            Delete Selected
          </button>
        </div>
      )}
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-700 bg-gray-900/50">
            <th className="px-4 py-3 w-12">
              <input
                type="checkbox"
                checked={selectAll && companies.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
              />
            </th>
            <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Company</th>
            <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Contact Name</th>
            <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Role</th>
            <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Email</th>
            <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Phone</th>
            <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Category</th>
            <th className="text-right px-4 py-3 text-sm text-gray-400 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies.map(company => (
            <tr key={company.id} className={`border-b border-gray-800 hover:bg-gray-900/50 transition-colors ${selectedIds.includes(company.id) ? 'bg-cyan-500/5' : ''}`}>
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(company.id)}
                  onChange={() => handleSelectOne(company.id)}
                  className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                    {(company.name || company.company_name)?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="text-white font-medium">{company.name || company.company_name}</div>
                    <div className="flex items-center gap-2">
                      {company.website && (
                        <a 
                          href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-400 hover:text-cyan-400 flex items-center gap-1"
                        >
                          <Globe size={10} />
                          {company.website}
                        </a>
                      )}
                      {company.contact_count > 0 && (
                        <span className="text-xs text-gray-500">
                          • {company.contact_count} contact{company.contact_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                {company.primary_contact ? (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {company.primary_contact.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                    </div>
                    <div>
                      <div className="text-white text-sm">{company.primary_contact.name}</div>
                      {company.primary_contact.linkedin_url && (
                        <a 
                          href={company.primary_contact.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">No contact</span>
                )}
              </td>
              <td className="px-4 py-3">
                {company.primary_contact?.title ? (
                  <span className="text-gray-300 text-sm">{company.primary_contact.title}</span>
                ) : (
                  <span className="text-gray-500 text-sm">-</span>
                )}
              </td>
              <td className="px-4 py-3">
                {company.primary_contact?.email ? (
                  <a 
                    href={`mailto:${company.primary_contact.email}`}
                    className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
                  >
                    <Mail size={12} />
                    {company.primary_contact.email}
                  </a>
                ) : (
                  <span className="text-gray-500 text-sm">-</span>
                )}
              </td>
              <td className="px-4 py-3">
                {company.primary_contact?.phone ? (
                  <a 
                    href={`tel:${company.primary_contact.phone}`}
                    className="text-gray-300 hover:text-white text-sm flex items-center gap-1"
                  >
                    <Phone size={12} />
                    {company.primary_contact.phone}
                  </a>
                ) : (
                  <span className="text-gray-500 text-sm">-</span>
                )}
              </td>
              <td className="px-4 py-3">
                {company.industry ? (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                    {company.industry}
                  </span>
                ) : (
                  <span className="text-gray-500 text-sm">-</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEdit(company)}
                    className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"
                    title="Edit Company"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => onCreateLead(company)}
                    className="p-2 hover:bg-green-500/20 rounded-lg text-gray-400 hover:text-green-400"
                    title="Add to Pipeline"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {companies.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Building className="mx-auto mb-3 text-gray-600" size={40} />
          <p>No companies found</p>
          <p className="text-sm mt-1">Add your first company to get started</p>
        </div>
      )}
    </div>
  );
};

// TheOrg Lead Finder Modal
const TheOrgLeadFinderModal = ({ 
  isOpen, 
  onClose, 
  theOrgSearch,
  setTheOrgSearch,
  theOrgIndustry,
  setTheOrgIndustry,
  theOrgResults,
  theOrgLoading,
  searchTheOrg,
  theOrgSelectedCompany,
  setTheOrgSelectedCompany,
  theOrgOrgChart,
  theOrgOrgChartLoading,
  fetchTheOrgOrgChart,
  importTheOrgLead,
  importingLead,
  theOrgCredits,
  fetchTheOrgCredits,
  theOrgExecutiveSearch,
  setTheOrgExecutiveSearch,
  enrichTheOrgContact,
  enrichingContact,
  enrichedContacts
}) => {
  useEffect(() => {
    if (isOpen) {
      fetchTheOrgCredits();
    }
  }, [isOpen, fetchTheOrgCredits]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Building className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">TheOrg Lead Finder</h2>
              <p className="text-sm text-gray-400">Search companies and import executive contacts</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {theOrgCredits && (
              <div className="text-sm text-gray-400">
                Credits: <span className="text-cyan-400 font-medium">{theOrgCredits.data?.planCredits ?? theOrgCredits.credits_remaining ?? 'N/A'}</span>
              </div>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-800 bg-gray-900/50">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              <input
                type="text"
                value={theOrgSearch}
                onChange={(e) => setTheOrgSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchTheOrg()}
                placeholder="Search company name (e.g., Salesforce, Google, Stripe)"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
              />
            </div>
            <select
              value={theOrgIndustry}
              onChange={(e) => setTheOrgIndustry(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500"
            >
              {INDUSTRIES.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
            <button
              onClick={searchTheOrg}
              disabled={theOrgLoading || !theOrgSearch.trim()}
              className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2"
            >
              {theOrgLoading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Search
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Company List */}
          <div className={`${theOrgSelectedCompany ? 'w-1/3 border-r border-gray-800' : 'w-full'} overflow-y-auto`}>
            {theOrgLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-cyan-400" />
              </div>
            ) : theOrgResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <Building size={48} className="mb-4 opacity-50" />
                <p>Search for companies to find leads</p>
                <p className="text-sm mt-1">Try searching "Salesforce" or "Tech startups"</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {theOrgResults.map((company, idx) => {
                  const companyDomain = company.domain || company.domains?.[0] || '';
                  const companyLogo = company.logo_url || company.logoUrl;
                  
                  return (
                    <div
                      key={company.id || idx}
                      onClick={() => {
                        setTheOrgSelectedCompany({...company, domain: companyDomain, logo_url: companyLogo});
                        setTheOrgExecutiveSearch(''); // Reset search when selecting new company
                        if (companyDomain) {
                          fetchTheOrgOrgChart(companyDomain);
                        }
                      }}
                      className={`p-4 cursor-pointer hover:bg-gray-800 transition-colors ${
                        theOrgSelectedCompany?.id === company.id ? 'bg-gray-800 border-l-2 border-cyan-500' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {companyLogo ? (
                          <img src={companyLogo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                            <Building size={20} className="text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{company.name}</h4>
                          {company.industry && (
                            <p className="text-xs text-gray-400">{company.industry}</p>
                          )}
                          {companyDomain && (
                            <p className="text-xs text-cyan-400">{companyDomain}</p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            importTheOrgLead({...company, domain: companyDomain});
                          }}
                          disabled={importingLead === companyDomain}
                          className="text-cyan-400 hover:text-cyan-300 p-1"
                          title="Import as Lead"
                        >
                          {importingLead === companyDomain ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Plus size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Org Chart / Executives Panel */}
          {theOrgSelectedCompany && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 border-b border-gray-800 bg-gray-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {theOrgSelectedCompany.logo_url ? (
                      <img src={theOrgSelectedCompany.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
                        <Building size={24} className="text-gray-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-white text-lg">{theOrgSelectedCompany.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        {theOrgSelectedCompany.industry && <span>{theOrgSelectedCompany.industry}</span>}
                        {theOrgSelectedCompany.employee_count && (
                          <>
                            <span>•</span>
                            <span>{theOrgSelectedCompany.employee_count} employees</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => importTheOrgLead(theOrgSelectedCompany)}
                    disabled={importingLead === theOrgSelectedCompany.domain}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                  >
                    {importingLead === theOrgSelectedCompany.domain ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Plus size={14} />
                    )}
                    Import Company
                  </button>
                </div>
              </div>

              {/* Executives List */}
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <Users size={14} />
                  Executives & Team ({theOrgOrgChart?.total_positions || 0})
                </h4>

                {/* Search Input for Executives */}
                {theOrgOrgChart?.executives?.length > 0 && (
                  <div className="mb-3">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        value={theOrgExecutiveSearch}
                        onChange={(e) => setTheOrgExecutiveSearch(e.target.value)}
                        placeholder="Search by name or role..."
                        className="w-full pl-9 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                        data-testid="theorg-executive-search"
                      />
                      {theOrgExecutiveSearch && (
                        <button
                          onClick={() => setTheOrgExecutiveSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {theOrgExecutiveSearch && (
                      <p className="text-xs text-gray-500 mt-1">
                        Showing {theOrgOrgChart.executives.filter(exec => {
                          const search = theOrgExecutiveSearch.toLowerCase();
                          const name = (exec.full_name || exec.fullName || '').toLowerCase();
                          const title = (exec.title || '').toLowerCase();
                          return name.includes(search) || title.includes(search);
                        }).length} of {theOrgOrgChart.total_positions} results
                      </p>
                    )}
                  </div>
                )}

                {theOrgOrgChartLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-cyan-400" />
                  </div>
                ) : theOrgOrgChart?.executives?.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {theOrgOrgChart.executives
                      .filter(exec => {
                        if (!theOrgExecutiveSearch) return true;
                        const search = theOrgExecutiveSearch.toLowerCase();
                        const name = (exec.full_name || exec.fullName || '').toLowerCase();
                        const title = (exec.title || '').toLowerCase();
                        return name.includes(search) || title.includes(search);
                      })
                      .slice(0, 100) // Limit to 100 results for performance
                      .map((exec, idx) => {
                        const execKey = `${theOrgSelectedCompany?.domain}-${exec.id}`;
                        const enrichedData = enrichedContacts[execKey];
                        const hasEmail = enrichedData?.email || exec.email;
                        const hasPhone = enrichedData?.phone;
                        
                        return (
                      <div
                        key={exec.id || idx}
                        className="p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                              {enrichedData?.profile_photo ? (
                                <img src={enrichedData.profile_photo} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Users size={14} className="text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{exec.full_name || exec.fullName}</p>
                              <p className="text-xs text-gray-400">{exec.title}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Unlock Contact Button */}
                            {!enrichedData && (
                              <button
                                onClick={() => enrichTheOrgContact(exec, theOrgSelectedCompany?.domain)}
                                disabled={enrichingContact === execKey}
                                className="text-yellow-400 hover:text-yellow-300 p-1 flex items-center gap-1"
                                title="Unlock contact info (uses 1 credit)"
                              >
                                {enrichingContact === execKey ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <>
                                    <Lock size={12} />
                                    <span className="text-xs">Unlock</span>
                                  </>
                                )}
                              </button>
                            )}
                            {(exec.linkedin_url || exec.linkedInUrl || enrichedData?.linkedin_url) && (
                              <a
                                href={enrichedData?.linkedin_url || exec.linkedin_url || exec.linkedInUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 p-1"
                                title="View LinkedIn"
                              >
                                <ExternalLink size={14} />
                              </a>
                            )}
                            <button
                              onClick={() => importTheOrgLead(theOrgSelectedCompany, {
                                ...exec,
                                email: enrichedData?.email || exec.email,
                                phone: enrichedData?.phone
                              })}
                              disabled={importingLead === execKey}
                              className="text-cyan-400 hover:text-cyan-300 p-1"
                              title="Import as Lead"
                            >
                              {importingLead === execKey ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Plus size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Enriched Contact Info */}
                        {enrichedData && (hasEmail || hasPhone) && (
                          <div className="mt-2 pt-2 border-t border-gray-700 flex flex-wrap gap-3">
                            {hasEmail && (
                              <a 
                                href={`mailto:${enrichedData.email || exec.email}`}
                                className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                              >
                                <Mail size={12} />
                                {enrichedData.email || exec.email}
                              </a>
                            )}
                            {hasPhone && (
                              <a 
                                href={`tel:${enrichedData.phone}`}
                                className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                              >
                                <Phone size={12} />
                                {enrichedData.phone}
                              </a>
                            )}
                          </div>
                        )}
                        
                        {/* No contact info message */}
                        {enrichedData && !hasEmail && !hasPhone && (
                          <div className="mt-2 pt-2 border-t border-gray-700">
                            <p className="text-xs text-gray-500">No email or phone available for this contact</p>
                          </div>
                        )}
                      </div>
                    )})}
                    {theOrgOrgChart.executives.filter(exec => {
                      if (!theOrgExecutiveSearch) return true;
                      const search = theOrgExecutiveSearch.toLowerCase();
                      const name = (exec.full_name || exec.fullName || '').toLowerCase();
                      const title = (exec.title || '').toLowerCase();
                      return name.includes(search) || title.includes(search);
                    }).length > 100 && (
                      <p className="text-xs text-gray-500 text-center py-2">
                        Showing first 100 results. Use search to find specific people.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No executives found</p>
                    <p className="text-sm mt-1">Org chart data may not be available for this company</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Data provided by TheOrg.com • Unlock contact = 1 credit • Org charts cost varies
          </p>
          <button
            onClick={onClose}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Lead Finder Modal
const LeadFinderModal = ({ isOpen, onClose, onLeadsFound }) => {
  const [step, setStep] = useState(1);
  const [searchMode, setSearchMode] = useState('companies'); // 'companies' or 'people'
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [keywords, setKeywords] = useState('');
  const [numLeads, setNumLeads] = useState(50);
  const [isSearching, setIsSearching] = useState(false);
  const [foundLeads, setFoundLeads] = useState([]);
  const [foundPeople, setFoundPeople] = useState([]);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [error, setError] = useState('');
  
  // People search fields
  const [personName, setPersonName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [numPeople, setNumPeople] = useState(10);

  // Toggle person selection
  const togglePersonSelection = (idx) => {
    if (selectedPeople.includes(idx)) {
      setSelectedPeople(selectedPeople.filter(i => i !== idx));
    } else {
      setSelectedPeople([...selectedPeople, idx]);
    }
  };

  // Add selected people to pipeline
  const handleAddSelectedToPipeline = async () => {
    if (selectedPeople.length === 0) return;
    
    let added = 0;
    for (const idx of selectedPeople) {
      const person = foundPeople[idx];
      if (person.company_id) {
        try {
          const response = await fetch(`${API_URL}/api/lead-gen/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              company_id: person.company_id,
              contact_id: person.id,
              stage: 'new',
              deal_value: 0,
              notes: `Added from People Finder - ${person.name} (${person.title})`
            })
          });
          if (response.ok) added++;
        } catch (err) {
          console.error('Failed to add lead:', err);
        }
      }
    }
    
    if (added > 0) {
      toast.success(`Added ${added} people to pipeline!`);
      onLeadsFound();
      setSelectedPeople([]);
    }
  };

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Real Estate', 'Retail',
    'Manufacturing', 'Education', 'Marketing', 'Legal', 'Consulting',
    'Construction', 'Transportation', 'Hospitality', 'Energy', 'Media',
    'Telecommunications', 'Insurance', 'Pharmaceutical', 'Automotive', 'Entertainment', 'Other'
  ];

  const companySizes = [
    { value: '', label: 'Any Size' },
    { value: '1-10', label: '1-10 employees (Startup)' },
    { value: '10-50', label: '10-50 employees (Small)' },
    { value: '50-200', label: '50-200 employees (Medium)' },
    { value: '200+', label: '200+ employees (Enterprise)' },
  ];

  const targetRoles = [
    'CEO / Founder', 'CTO / Technical Lead', 'CMO / Marketing Director',
    'CFO / Finance Director', 'VP of Sales', 'Head of Operations',
    'HR Director', 'Procurement Manager', 'IT Director', 'Other'
  ];

  const handleSearch = async () => {
    if (!industry) {
      setError('Please select an industry');
      return;
    }
    
    setIsSearching(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/find-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          industry,
          location: location || null,
          company_size: companySize || null,
          target_role: targetRole || null,
          keywords: keywords || null,
          num_leads: numLeads
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFoundLeads(data.companies || []);
        setStep(3);
        toast.success(`Found ${data.total_added} companies!`);
      } else {
        setError(data.detail || 'Failed to find leads');
      }
    } catch (err) {
      setError('Failed to connect to lead finder');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePeopleSearch = async () => {
    if (!personName && !companyName && !jobTitle && !industry) {
      setError('Please provide at least one search criteria');
      return;
    }
    
    setIsSearching(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/find-people`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_name: personName || null,
          company_name: companyName || null,
          job_title: jobTitle || null,
          industry: industry || null,
          location: location || null,
          num_results: numPeople
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFoundPeople(data.people || []);
        setStep(3);
        toast.success(`Found ${data.total_found} people with emails!`);
      } else {
        setError(data.detail || 'Failed to find people');
      }
    } catch (err) {
      setError('Failed to connect to people finder');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSearchMode('companies');
    setIndustry('');
    setLocation('');
    setCompanySize('');
    setTargetRole('');
    setKeywords('');
    setPersonName('');
    setCompanyName('');
    setJobTitle('');
    setFoundLeads([]);
    setFoundPeople([]);
    setSelectedPeople([]);
    setError('');
    onClose();
    if (foundLeads.length > 0 || foundPeople.length > 0) {
      onLeadsFound();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="text-purple-400" size={24} />
              AI Lead Finder
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {searchMode === 'companies' ? (
                <>
                  {step === 1 && 'Select an industry to search'}
                  {step === 2 && 'Narrow your search criteria'}
                  {step === 3 && `Found ${foundLeads.length} companies`}
                </>
              ) : (
                <>
                  {step === 1 && 'Search for specific people'}
                  {step === 2 && 'Add more details'}
                  {step === 3 && `Found ${foundPeople.length} people with emails`}
                </>
              )}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Search Mode Toggle */}
        {step === 1 && (
          <div className="px-6 pt-4">
            <div className="flex bg-gray-800 rounded-lg p-1 gap-1">
              <button
                onClick={() => setSearchMode('companies')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  searchMode === 'companies'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Building size={16} />
                Find Companies
              </button>
              <button
                onClick={() => setSearchMode('people')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  searchMode === 'people'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Users size={16} />
                Find People & Emails
              </button>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-500'
                }`}>
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 mx-1 rounded ${step > s ? 'bg-purple-500' : 'bg-gray-800'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
            {searchMode === 'companies' ? (
              <>
                <span>Industry</span>
                <span>Details</span>
                <span>Results</span>
              </>
            ) : (
              <>
                <span>Search</span>
                <span>Refine</span>
                <span>Results</span>
              </>
            )}
          </div>
        </div>

        {/* COMPANIES MODE - Step 1: Industry Selection */}
        {searchMode === 'companies' && step === 1 && (
          <div className="p-6">
            <label className="block text-sm text-gray-400 mb-3">Select Industry *</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {industries.map((ind) => (
                <button
                  key={ind}
                  onClick={() => { setIndustry(ind); setStep(2); }}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    industry === ind
                      ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                      : 'border-gray-700 hover:border-gray-600 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <span className="font-medium">{ind}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* PEOPLE MODE - Step 1: Search Criteria */}
        {searchMode === 'people' && step === 1 && (
          <div className="p-6 space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <Mail className="text-blue-400" size={18} />
                <span className="text-blue-400 text-sm">Find specific people and their business email addresses</span>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-400 mb-2">Person's Name</label>
              <input
                type="text"
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., John Smith"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., Microsoft, Tesla, or any company"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Job Title</label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., CEO, Marketing Director, VP of Sales"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Industry (Optional)</label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                >
                  <option value="">Any Industry</option>
                  {industries.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Location (Optional)</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                  placeholder="e.g., New York"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Number of Results</label>
              <div className="flex gap-2">
                {[5, 10, 20, 50].map((num) => (
                  <button
                    key={num}
                    onClick={() => setNumPeople(num)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      numPeople === num
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handlePeopleSearch}
                disabled={isSearching || (!personName && !companyName && !jobTitle && !industry)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isSearching ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search size={18} />
                    Find People & Emails
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* COMPANIES MODE - Step 2: Search Criteria */}
        {searchMode === 'companies' && step === 2 && (
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <Tag className="text-purple-400" size={18} />
                <span className="text-purple-400 font-medium">{industry}</span>
                <button 
                  onClick={() => setStep(1)}
                  className="ml-auto text-xs text-gray-400 hover:text-white"
                >
                  Change
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., Dallas, TX or Texas or United States"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Company Size</label>
              <select
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
              >
                {companySizes.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Target Decision Maker Role</label>
              <select
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
              >
                <option value="">Any Role</option>
                {targetRoles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Keywords / Focus Area</label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-purple-500 outline-none"
                placeholder="e.g., SaaS, e-commerce, B2B, healthcare tech"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Number of Leads to Find</label>
              <div className="flex items-center gap-4">
                {[25, 50, 100].map((n) => (
                  <button
                    key={n}
                    onClick={() => setNumLeads(n)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      numLeads === n
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Results - Companies */}
        {step === 3 && searchMode === 'companies' && (
          <div className="p-6">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <span className="text-green-400 font-medium">
                  Successfully added {foundLeads.length} companies to your Target List!
                </span>
              </div>
            </div>

            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {foundLeads.slice(0, 10).map((company, idx) => (
                <div key={idx} className="bg-gray-800 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold">
                    {company.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{company.name}</div>
                    <div className="text-xs text-gray-400 truncate">
                      {company.location} • {company.size}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    company.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                    company.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {company.priority}
                  </span>
                </div>
              ))}
              {foundLeads.length > 10 && (
                <div className="text-center text-gray-500 text-sm py-2">
                  +{foundLeads.length - 10} more companies added
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Results - People */}
        {step === 3 && searchMode === 'people' && (
          <div className="p-6">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <div>
                  <span className="text-green-400 font-medium">
                    Found {foundPeople.length} people with email addresses!
                  </span>
                  <p className="text-green-400/70 text-sm mt-1">
                    Companies and contacts have been added to your Target List automatically.
                  </p>
                </div>
              </div>
            </div>

            {/* Selection bar */}
            {selectedPeople.length > 0 && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-4 flex items-center justify-between">
                <span className="text-purple-400 font-medium">
                  {selectedPeople.length} {selectedPeople.length === 1 ? 'person' : 'people'} selected
                </span>
                <button
                  onClick={handleAddSelectedToPipeline}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 font-medium transition-colors"
                >
                  <Plus size={16} />
                  Add to Pipeline
                </button>
              </div>
            )}

            <div className="max-h-[400px] overflow-y-auto space-y-3">
              {foundPeople.map((person, idx) => (
                <div 
                  key={idx} 
                  className={`bg-gray-800 rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedPeople.includes(idx) ? 'ring-2 ring-purple-500 bg-purple-500/10' : 'hover:bg-gray-750'
                  }`}
                  onClick={() => togglePersonSelection(idx)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedPeople.includes(idx)}
                        onChange={() => togglePersonSelection(idx)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                      />
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {person.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-white">{person.name}</div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          person.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                          person.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {person.confidence} confidence
                        </span>
                      </div>
                      <div className="text-sm text-purple-400">{person.title}</div>
                      <div className="text-sm text-gray-400">{person.company_name}</div>
                      
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a 
                          href={`mailto:${person.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                        >
                          <Mail size={14} />
                          {person.email}
                        </a>
                        {person.linkedin_url && (
                          <a 
                            href={person.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
                          >
                            <ExternalLink size={14} />
                            LinkedIn
                          </a>
                        )}
                      </div>
                      
                      {person.background && (
                        <div className="mt-2 text-xs text-gray-500 line-clamp-2">
                          {person.background}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Select All / View Target List */}
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => {
                  if (selectedPeople.length === foundPeople.length) {
                    setSelectedPeople([]);
                  } else {
                    setSelectedPeople(foundPeople.map((_, idx) => idx));
                  }
                }}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {selectedPeople.length === foundPeople.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => { handleClose(); /* Switch to Target List tab */ }}
                className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
              >
                View in Target List
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          {step === 2 && searchMode === 'companies' && (
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Back
            </button>
          )}
          {(step !== 2 || searchMode === 'people') && <div />}
          
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              {step === 3 ? 'Done' : 'Cancel'}
            </button>
            {step === 2 && searchMode === 'companies' && (
              <button
                onClick={handleSearch}
                disabled={isSearching || !industry}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {isSearching ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Finding Leads...
                  </>
                ) : (
                  <>
                    <Search size={18} />
                    Find {numLeads} Leads
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// CSV Import Modal
const CSVImportModal = ({ isOpen, onClose, onImport }) => {
  const [csvData, setCsvData] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  const parseCSV = () => {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) {
      toast.error('CSV must have headers and at least one data row');
      return;
    }
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx]?.trim() || '';
      });
      data.push(row);
    }
    
    setParsedData(data);
    toast.success(`Parsed ${data.length} rows`);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('Please parse CSV first');
      return;
    }
    setIsImporting(true);
    try {
      await onImport(parsedData);
      setCsvData('');
      setParsedData([]);
      onClose();
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Upload className="text-cyan-400" size={24} />
            Import Companies from CSV
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Paste CSV Data (with headers: name, website, industry, location, size)
            </label>
            <textarea
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none font-mono text-sm"
              rows={10}
              placeholder="name,website,industry,location,size&#10;Acme Inc,www.acme.com,Technology,Dallas TX,50-200&#10;..."
            />
          </div>

          {parsedData.length > 0 && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="text-green-400 font-medium mb-2">
                ✓ {parsedData.length} companies ready to import
              </div>
              <div className="text-sm text-gray-400">
                First company: {parsedData[0].name || parsedData[0].company || 'Unknown'}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={parseCSV}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Parse CSV
          </button>
          <button
            onClick={handleImport}
            disabled={parsedData.length === 0 || isImporting}
            className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50"
          >
            {isImporting ? 'Importing...' : `Import ${parsedData.length} Companies`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Lead Modal
const EditLeadModal = ({ isOpen, onClose, lead, onSave, onDelete }) => {
  const [form, setForm] = useState({
    deal_value: 0,
    notes: '',
    tags: [],
    next_followup_date: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        deal_value: lead.deal_value || 0,
        notes: lead.notes || '',
        tags: lead.tags || [],
        next_followup_date: lead.next_followup_date ? lead.next_followup_date.split('T')[0] : ''
      });
    }
  }, [lead]);

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({ ...lead, ...form });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !lead) return null;

  const stageConfig = PIPELINE_STAGES.find(s => s.id === lead.stage) || PIPELINE_STAGES[0];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Edit className="text-cyan-400" size={24} />
              Edit Lead
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {lead.company?.name || 'Unknown Company'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Lead Info */}
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-3 h-3 rounded-full ${stageConfig.color}`} />
              <span className="font-medium text-white">{stageConfig.label}</span>
              {lead.company?.priority && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  lead.company.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                  lead.company.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  Priority {lead.company.priority}
                </span>
              )}
            </div>
            {lead.contact && (
              <div className="text-sm text-gray-400">
                Contact: {lead.contact.name} {lead.contact.email && `(${lead.contact.email})`}
              </div>
            )}
          </div>

          {/* Deal Value */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Deal Value ($)</label>
            <input
              type="number"
              value={form.deal_value}
              onChange={(e) => setForm({ ...form, deal_value: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              placeholder="15000"
            />
          </div>

          {/* Next Follow-up */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Next Follow-up Date</label>
            <input
              type="date"
              value={form.next_followup_date}
              onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Tags</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {form.tags.map((tag, idx) => (
                <span 
                  key={idx} 
                  className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm flex items-center gap-1"
                >
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-white">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                className="flex-1 px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="Add a tag..."
              />
              <button
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none"
              rows={4}
              placeholder="Add notes about this lead..."
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-700">
          <button
            onClick={() => { onDelete(lead); onClose(); }}
            className="px-4 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors flex items-center gap-2"
          >
            <Trash2 size={16} />
            Delete Lead
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Queue Item Modal
const EditQueueModal = ({ isOpen, onClose, queueItem, onSave }) => {
  const [form, setForm] = useState({
    subject: '',
    body: '',
    recipient_email: '',
    recipient_name: '',
    scheduled_date: '',
    scheduled_time: '09:00'
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (queueItem) {
      setForm({
        subject: queueItem.subject || '',
        body: queueItem.body || '',
        recipient_email: queueItem.recipient_email || '',
        recipient_name: queueItem.recipient_name || '',
        scheduled_date: queueItem.scheduled_date || '',
        scheduled_time: queueItem.scheduled_time || '09:00'
      });
    }
  }, [queueItem]);

  const handleSave = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ ...queueItem, ...form });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !queueItem) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Edit className="text-cyan-400" size={24} />
              Edit Email
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {queueItem.company?.name || 'Unknown Company'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Recipient Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Recipient Name</label>
              <input
                type="text"
                value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Recipient Email *</label>
              <input
                type="email"
                value={form.recipient_email}
                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                placeholder="john@company.com"
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Subject Line *</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              placeholder="Quick idea for your growth..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {form.subject.length}/50 characters (recommended max)
            </p>
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email Body *</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none font-mono text-sm"
              rows={12}
              placeholder="Write your email content here..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {form.body.split(/\s+/).filter(w => w).length} words
            </p>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Scheduled Date</label>
              <input
                type="date"
                value={form.scheduled_date}
                onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Scheduled Time</label>
              <input
                type="time"
                value={form.scheduled_time}
                onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !form.subject.trim() || !form.body.trim()}
            className="px-6 py-2 bg-cyan-500 text-black font-medium rounded-lg hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============

const UltimateLeadGeneration = () => {
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);

  // Gmail/Email Queue State
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [emailQueue, setEmailQueue] = useState([]);
  const [emailQueueLoading, setEmailQueueLoading] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  // Filters
  const [industryFilter, setIndustryFilter] = useState('All');
  const [sizeFilter, setSizeFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyModalMode, setCompanyModalMode] = useState('create');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showEditQueueModal, setShowEditQueueModal] = useState(false);
  const [selectedQueueItem, setSelectedQueueItem] = useState(null);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [leadToView, setLeadToView] = useState(null);
  const [showLeadFinderModal, setShowLeadFinderModal] = useState(false);
  
  // Email Send Modal State
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState(null);
  const [selectedFromAccount, setSelectedFromAccount] = useState('creatrhq');
  const [sendSchedule, setSendSchedule] = useState('now');
  const [scheduledTime, setScheduledTime] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  // TheOrg Lead Finder State
  const [showTheOrgModal, setShowTheOrgModal] = useState(false);
  const [theOrgSearch, setTheOrgSearch] = useState('');
  const [theOrgIndustry, setTheOrgIndustry] = useState('All');
  const [theOrgResults, setTheOrgResults] = useState([]);
  const [theOrgLoading, setTheOrgLoading] = useState(false);
  const [theOrgSelectedCompany, setTheOrgSelectedCompany] = useState(null);
  const [theOrgOrgChart, setTheOrgOrgChart] = useState(null);
  const [theOrgOrgChartLoading, setTheOrgOrgChartLoading] = useState(false);
  const [theOrgCredits, setTheOrgCredits] = useState(null);
  const [importingLead, setImportingLead] = useState(null);
  const [theOrgExecutiveSearch, setTheOrgExecutiveSearch] = useState('');
  const [enrichingContact, setEnrichingContact] = useState(null);
  const [enrichedContacts, setEnrichedContacts] = useState({});

  // Fetch data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/dashboard`);
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (industryFilter !== 'All') params.append('industry', industryFilter);
      if (sizeFilter !== 'All') params.append('size', sizeFilter);
      if (priorityFilter !== 'All') params.append('priority', priorityFilter);
      
      const response = await fetch(`${API_URL}/api/lead-gen/companies?${params}`);
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, [industryFilter, sizeFilter, priorityFilter]);

  const fetchPipeline = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/pipeline`);
      const data = await response.json();
      setPipeline(data.pipeline || {});
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    }
  }, []);

  // Fetch Gmail status
  const fetchGmailStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/gmail/status?account_id=creatrhq`);
      const data = await response.json();
      setGmailStatus(data);
      
      // Also fetch all Gmail accounts
      const accountsResponse = await fetch(`${API_URL}/api/gmail/accounts`);
      const accountsData = await accountsResponse.json();
      setGmailAccounts(accountsData.accounts || []);
    } catch (error) {
      console.error('Failed to fetch Gmail status:', error);
    }
  }, []);

  // Fetch email queue
  const fetchEmailQueue = useCallback(async () => {
    setEmailQueueLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue`);
      const data = await response.json();
      setEmailQueue(data.queue || []);
    } catch (error) {
      console.error('Failed to fetch email queue:', error);
    } finally {
      setEmailQueueLoading(false);
    }
  }, []);

  // TheOrg Functions
  const searchTheOrg = async () => {
    if (!theOrgSearch.trim()) {
      toast.error('Please enter a company name to search');
      return;
    }
    
    setTheOrgLoading(true);
    setTheOrgResults([]);
    setTheOrgSelectedCompany(null);
    setTheOrgOrgChart(null);
    
    try {
      const params = new URLSearchParams({
        query: theOrgSearch,
        limit: '20'
      });
      if (theOrgIndustry !== 'All') {
        params.append('industry', theOrgIndustry);
      }
      
      const response = await fetch(`${API_URL}/api/theorg/search/companies?${params}`);
      const data = await response.json();
      
      if (response.ok) {
        setTheOrgResults(data.companies || []);
        if (data.companies?.length === 0) {
          toast.info('No companies found. Try a different search term.');
        }
      } else {
        toast.error(data.detail || 'Search failed');
      }
    } catch (error) {
      console.error('TheOrg search error:', error);
      toast.error('Failed to search TheOrg');
    } finally {
      setTheOrgLoading(false);
    }
  };

  const fetchTheOrgOrgChart = async (domain) => {
    setTheOrgOrgChartLoading(true);
    setTheOrgOrgChart(null);
    
    console.log('[TheOrg] Fetching org chart for domain:', domain);
    
    try {
      const url = `${API_URL}/api/theorg/company/${domain}/chart`;
      console.log('[TheOrg] API URL:', url);
      
      const response = await fetch(url);
      const status = response.status;
      console.log('[TheOrg] Response status:', status);
      
      // Handle non-OK responses first before trying to read body
      if (status === 402) {
        toast.error('TheOrg API credits exhausted. Please add more credits to your account.', {
          duration: 5000
        });
        fetchTheOrgCredits();
        return;
      }
      
      if (status === 404) {
        toast.error('Company org chart not found on TheOrg.com');
        return;
      }
      
      if (status === 401) {
        toast.error('TheOrg API key is invalid');
        return;
      }
      
      if (!response.ok) {
        // Try to get error detail, but don't fail if body already consumed
        try {
          const data = await response.json();
          toast.error(data.detail || `Failed to fetch org chart (${status})`);
        } catch {
          toast.error(`Failed to fetch org chart (${status})`);
        }
        return;
      }
      
      // Only try to parse JSON for successful responses
      const data = await response.json();
      console.log('[TheOrg] Response data:', data);
      
      setTheOrgOrgChart(data);
      toast.success(`Found ${data.total_positions || 0} executives`);
      
    } catch (error) {
      console.error('TheOrg org chart error:', error);
      // Check if it's a response-related error (body already used)
      if (error.message?.includes('Response body') || error.message?.includes('clone')) {
        // The API call likely succeeded but response was consumed by recorder
        // Check if we got a 402 status from the logs
        toast.error('TheOrg API credits may be exhausted. Please check your account.');
        fetchTheOrgCredits();
      } else {
        toast.error('Failed to fetch organization chart - Network error');
      }
    } finally {
      setTheOrgOrgChartLoading(false);
    }
  };

  const fetchTheOrgCredits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/theorg/usage`);
      const data = await response.json();
      setTheOrgCredits(data);
    } catch (error) {
      console.error('Failed to fetch TheOrg credits:', error);
    }
  };

  const importTheOrgLead = async (company, executive = null) => {
    const importKey = executive ? `${company.domain}-${executive.id}` : company.domain;
    setImportingLead(importKey);
    
    try {
      const params = new URLSearchParams({
        company_domain: company.domain || company.name?.toLowerCase().replace(/\s+/g, '') + '.com',
        company_name: company.name || ''
      });
      
      if (executive) {
        params.append('executive_name', executive.full_name || executive.fullName || '');
        params.append('executive_title', executive.title || '');
        params.append('executive_linkedin', executive.linkedin_url || executive.linkedInUrl || '');
        params.append('executive_email', executive.email || executive.workEmail || '');
        params.append('executive_phone', executive.phone || '');
      }
      
      const response = await fetch(`${API_URL}/api/theorg/import-lead?${params}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Lead imported: ${company.name || company.domain}`);
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to import lead');
      }
    } catch (error) {
      console.error('Import lead error:', error);
      toast.error('Failed to import lead');
    } finally {
      setImportingLead(null);
    }
  };

  // Enrich contact with detailed info (email, phone) using TheOrg credits
  const enrichTheOrgContact = async (executive, companyDomain) => {
    const execKey = `${companyDomain}-${executive.id}`;
    setEnrichingContact(execKey);
    
    try {
      const personName = executive.full_name || executive.fullName;
      const params = new URLSearchParams({
        person_name: personName
      });
      if (companyDomain) {
        params.append('company_domain', companyDomain);
      }
      
      const response = await fetch(`${API_URL}/api/theorg/contact/enrich?${params}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok && data.found) {
        // Store enriched contact data
        setEnrichedContacts(prev => ({
          ...prev,
          [execKey]: data.contact
        }));
        
        const email = data.contact.email;
        const phone = data.contact.phone;
        
        if (email || phone) {
          toast.success(`Contact info found! ${email ? `Email: ${email}` : ''} ${phone ? `Phone: ${phone}` : ''}`, {
            duration: 5000
          });
        } else {
          toast.info('Contact found but no email/phone available');
        }
        
        // Refresh credits display
        fetchTheOrgCredits();
      } else if (response.status === 402) {
        toast.error('Insufficient TheOrg credits. Please add more credits to your account.');
      } else if (!data.found) {
        toast.info('No contact details found for this person');
      } else {
        toast.error(data.detail || 'Failed to get contact details');
      }
    } catch (error) {
      console.error('Enrich contact error:', error);
      toast.error('Failed to get contact details');
    } finally {
      setEnrichingContact(null);
    }
  };

  const exportLeadsToCSV = () => {
    if (companies.length === 0) {
      toast.error('No leads to export');
      return;
    }
    
    const headers = ['Company', 'Contact', 'Title', 'Email', 'Phone', 'Industry', 'Stage', 'Priority', 'Deal Value', 'Source'];
    const rows = companies.map(c => [
      c.company_name || '',
      c.contact_name || '',
      c.contact_title || '',
      c.contact_email || '',
      c.contact_phone || '',
      c.industry || '',
      c.stage || '',
      c.priority || '',
      c.deal_value || 0,
      c.source || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Leads exported to CSV');
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchCompanies(), fetchPipeline(), fetchGmailStatus(), fetchEmailQueue()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchCompanies, fetchPipeline, fetchGmailStatus, fetchEmailQueue]);

  // Handlers
  const handleSaveCompany = async (formData) => {
    try {
      const url = companyModalMode === 'create'
        ? `${API_URL}/api/lead-gen/companies`
        : `${API_URL}/api/lead-gen/companies/${selectedCompany.id}`;
      
      const response = await fetch(url, {
        method: companyModalMode === 'create' ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(companyModalMode === 'create' ? 'Company added!' : 'Company updated!');
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to save company');
      }
    } catch (error) {
      toast.error('Failed to save company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Delete this company and all related data?')) return;
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/${companyId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Company deleted');
        fetchCompanies();
        fetchPipeline();
        fetchDashboard();
        setShowCompanyModal(false);
      }
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const handleBulkDeleteCompanies = async (companyIds) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_ids: companyIds })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Deleted ${data.deleted_count} companies`);
        fetchCompanies();
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to delete companies');
      }
    } catch (error) {
      toast.error('Failed to delete companies');
    }
  };

  const handleEnrichCompany = async (company) => {
    if (!company.website) {
      toast.error('Company has no website to enrich');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/companies/${company.id}/enrich`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Company enriched!');
        fetchCompanies();
      } else {
        toast.error(data.detail || 'Failed to enrich');
      }
    } catch (error) {
      toast.error('Failed to enrich company');
    }
  };

  const handleCreateLead = async (company) => {
    const companyName = company.name || company.company_name || 'Company';
    try {
      // Include the primary contact if available
      const leadData = {
        company_id: company.id,
        stage: 'new',
        deal_value: 0
      };
      
      // If company has a primary contact, link it to the lead
      if (company.primary_contact?.id) {
        leadData.contact_id = company.primary_contact.id;
      }
      
      const response = await fetch(`${API_URL}/api/lead-gen/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });
      if (response.ok) {
        toast.success(`${companyName} added to pipeline!`);
        fetchPipeline();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const { draggableId, source, destination } = result;
    
    if (source.droppableId === destination.droppableId) return;
    
    // Optimistic update
    const newPipeline = { ...pipeline };
    const sourceLead = newPipeline[source.droppableId].find(l => l.id === draggableId);
    newPipeline[source.droppableId] = newPipeline[source.droppableId].filter(l => l.id !== draggableId);
    sourceLead.stage = destination.droppableId;
    newPipeline[destination.droppableId] = [...(newPipeline[destination.droppableId] || []), sourceLead];
    setPipeline(newPipeline);
    
    // API call
    try {
      await fetch(`${API_URL}/api/lead-gen/leads/${draggableId}/stage?new_stage=${destination.droppableId}`, {
        method: 'PATCH'
      });
      toast.success(`Lead moved to ${PIPELINE_STAGES.find(s => s.id === destination.droppableId)?.label}`);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to move lead');
      fetchPipeline();
    }
  };

  const handleGenerateEmail = (lead) => {
    setSelectedLead(lead);
    setShowEmailModal(true);
  };

  // View lead details handler
  const handleViewLeadDetails = (lead) => {
    setLeadToView(lead);
    setShowLeadDetailModal(true);
  };

  // Edit lead handler
  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setShowEditLeadModal(true);
  };

  // Update lead handler
  const handleUpdateLead = async (updatedLead) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/leads/${updatedLead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deal_value: updatedLead.deal_value,
          notes: updatedLead.notes,
          tags: updatedLead.tags,
          next_followup_date: updatedLead.next_followup_date
        })
      });
      if (response.ok) {
        toast.success('Lead updated!');
        fetchPipeline();
        fetchDashboard();
        setShowEditLeadModal(false);
      } else {
        toast.error('Failed to update lead');
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  // Delete lead handler
  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Delete lead for ${lead.company?.name || 'Unknown Company'}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/leads/${lead.id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Lead deleted');
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error('Failed to delete lead');
      }
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleApproveEmail = async (emailData) => {
    if (!selectedLead) return;
    
    // Get recipient email from contact or company
    const recipientEmail = selectedLead.contact?.email || '';
    
    if (!recipientEmail) {
      toast.error('No email address found for this lead. Please add a contact with email.');
      return;
    }
    
    try {
      // Add to email queue
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: selectedLead.id,
          subject: emailData.subject,
          body: emailData.body,
          scheduled_date: new Date().toISOString().split('T')[0],
          scheduled_time: '09:00',
          recipient_email: recipientEmail,
          recipient_name: selectedLead.contact?.name || selectedLead.company?.name
        })
      });
      
      if (response.ok) {
        toast.success('Email added to queue!');
        fetchEmailQueue();
        fetchDashboard();
      } else {
        toast.error('Failed to queue email');
      }
    } catch (error) {
      toast.error('Failed to queue email');
    }
  };

  // Send email via Gmail
  // Open email send modal
  const openSendEmailModal = (queueItem) => {
    setEmailToSend(queueItem);
    setEditedSubject(queueItem.subject || '');
    setEditedBody(queueItem.body || '');
    setSelectedFromAccount('creatrhq');
    setSendSchedule('now');
    setScheduledTime('');
    setShowSendEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailToSend) return;
    
    const selectedAccount = gmailAccounts.find(a => a.account_id === selectedFromAccount);
    if (!selectedAccount?.connected) {
      toast.error('Please select a connected email account');
      return;
    }
    
    setSendingEmailId(emailToSend.id);
    try {
      // If scheduled for later, update the queue item
      if (sendSchedule === 'later' && scheduledTime) {
        const scheduleResponse = await fetch(`${API_URL}/api/lead-gen/email-queue/${emailToSend.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheduled_time: scheduledTime,
            subject: editedSubject,
            body: editedBody,
            from_account: selectedFromAccount
          })
        });
        
        if (scheduleResponse.ok) {
          toast.success(`Email scheduled for ${new Date(scheduledTime).toLocaleString()}`);
          setShowSendEmailModal(false);
          fetchEmailQueue();
          return;
        }
      }
      
      // Send immediately
      const response = await fetch(`${API_URL}/api/gmail/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailToSend.recipient_email,
          subject: editedSubject,
          body: editedBody,
          account_id: selectedFromAccount
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Email sent successfully!');
        
        // Remove from queue
        await fetch(`${API_URL}/api/lead-gen/email-queue/${emailToSend.id}`, {
          method: 'DELETE'
        });
        
        // Update lead stage to "contacted" if it was "new"
        if (emailToSend.lead?.stage === 'new') {
          await fetch(`${API_URL}/api/lead-gen/leads/${emailToSend.lead_id}/stage?new_stage=contacted`, {
            method: 'PATCH'
          });
        }
        
        setShowSendEmailModal(false);
        fetchEmailQueue();
        fetchPipeline();
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmailId(null);
    }
  };

  // Remove email from queue
  const handleRemoveFromQueue = async (queueId) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue/${queueId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Removed from queue');
        fetchEmailQueue();
      }
    } catch (error) {
      toast.error('Failed to remove from queue');
    }
  };

  // Update email in queue
  const handleUpdateQueueItem = async (updatedItem) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/email-queue/${updatedItem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedItem)
      });
      if (response.ok) {
        toast.success('Email updated!');
        fetchEmailQueue();
        setShowEditQueueModal(false);
      } else {
        toast.error('Failed to update email');
      }
    } catch (error) {
      toast.error('Failed to update email');
    }
  };

  // Open edit modal for queue item
  const handleEditQueueItem = (item) => {
    setSelectedQueueItem(item);
    setShowEditQueueModal(true);
  };

  const handleImportCSV = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/lead-gen/import-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Imported ${result.imported} companies!`);
        fetchCompanies();
        fetchDashboard();
      } else {
        toast.error(result.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
    }
  };

  // Filter companies by search
  const filteredCompanies = companies.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = (c.name || c.company_name || '').toLowerCase();
    const website = (c.website || '').toLowerCase();
    const domain = (c.domain || '').toLowerCase();
    return name.includes(query) || website.includes(query) || domain.includes(query);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-black/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
                <Target className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Lead Generation</h1>
                <p className="text-xs sm:text-sm text-gray-400">Build, research, outreach, and close</p>
              </div>
            </div>
            
            {/* Action Buttons - Horizontal scroll on mobile */}
            <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0 -mx-3 px-3 sm:mx-0 sm:px-0">
              <button
                onClick={() => setShowTheOrgModal(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 rounded-lg text-white text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0"
              >
                <Building size={16} />
                <span className="hidden xs:inline">TheOrg</span> Search
              </button>
              <button
                onClick={() => setShowLeadFinderModal(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 rounded-lg text-white text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0"
              >
                <Sparkles size={16} />
                <span className="hidden xs:inline">Find</span> Leads
              </button>
              <button
                onClick={exportLeadsToCSV}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 transition-colors whitespace-nowrap"
              >
                <Download size={18} />
                Export
              </button>
              <button
                onClick={() => setShowCSVModal(true)}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 transition-colors whitespace-nowrap"
              >
                <Upload size={18} />
                Import
              </button>
              <button
                onClick={() => {
                  setSelectedCompany(null);
                  setCompanyModalMode('create');
                  setShowCompanyModal(true);
                }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-lg text-black text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">Add</span> Company
              </button>
            </div>
          </div>

          {/* Tabs - Horizontally scrollable on mobile */}
          <div className="flex gap-1 mt-3 sm:mt-4 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 pb-1 sm:pb-0">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'pipeline', label: 'Pipeline', icon: TrendingUp },
              { id: 'companies', label: 'Target List', icon: Building },
              { id: 'queue', label: 'Email Queue', icon: Mail },
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <>
            <WeeklyMissionPanel mission={dashboard.weekly_mission} />
            <TodaysActionsPanel actions={dashboard.todays_actions} onRefresh={fetchDashboard} />
            <MetricsPanel metrics={dashboard.metrics} />
          </>
        )}

        {/* Pipeline Tab */}
        {activeTab === 'pipeline' && (
          <PipelineBoard
            pipeline={pipeline}
            onDragEnd={handleDragEnd}
            onEditLead={handleEditLead}
            onGenerateEmail={handleGenerateEmail}
            onDeleteLead={handleDeleteLead}
            onViewLeadDetails={handleViewLeadDetails}
          />
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <>
            {/* Filters */}
            <div className="bg-black border border-gray-700 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Search className="text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search companies..."
                  className="bg-transparent border-none outline-none text-white placeholder-gray-500 w-48"
                />
              </div>
              <div className="h-6 w-px bg-gray-700" />
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              <select
                value={sizeFilter}
                onChange={(e) => setSizeFilter(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                {COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="All">All Priorities</option>
                {PRIORITIES.map(p => <option key={p} value={p}>Priority {p}</option>)}
              </select>
              <div className="ml-auto text-sm text-gray-400">
                {filteredCompanies.length} companies
              </div>
            </div>

            <CompaniesTable
              companies={filteredCompanies}
              onEdit={(company) => {
                setSelectedCompany(company);
                setCompanyModalMode('edit');
                setShowCompanyModal(true);
              }}
              onCreateLead={handleCreateLead}
              onEnrich={handleEnrichCompany}
              onBulkDelete={handleBulkDeleteCompanies}
            />
          </>
        )}

        {/* Email Queue Tab */}
        {activeTab === 'queue' && (
          <div className="space-y-6">
            {/* Gmail Connection Status */}
            <div className="bg-black border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    gmailStatus?.connected ? 'bg-red-500/20' : 'bg-gray-800'
                  }`}>
                    <Mail className={gmailStatus?.connected ? 'text-red-400' : 'text-gray-500'} size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white">Gmail Connection</h3>
                    {gmailStatus?.connected ? (
                      <p className="text-sm text-green-400 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Connected as {gmailStatus.email}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400">Not connected</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {gmailStatus?.connected ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                        Active
                      </span>
                    </div>
                  ) : (
                    <a
                      href={`${API_URL}/api/gmail/auth/url?account_id=creatrhq`}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <Mail size={18} />
                      Connect Gmail
                    </a>
                  )}
                  <button
                    onClick={fetchGmailStatus}
                    className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                  >
                    <RefreshCw size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Email Queue */}
            <div className="bg-black border border-gray-700 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <Send className="text-cyan-400" size={20} />
                  Email Queue
                  <span className="text-sm text-gray-400 ml-2">({emailQueue.length} emails)</span>
                </h3>
                <button
                  onClick={fetchEmailQueue}
                  className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                >
                  <RefreshCw size={18} />
                </button>
              </div>

              {emailQueueLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto" />
                </div>
              ) : emailQueue.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail className="mx-auto text-gray-600 mb-4" size={48} />
                  <h4 className="text-lg font-medium text-white mb-2">No emails in queue</h4>
                  <p className="text-gray-400 max-w-md mx-auto">
                    Generate and approve email drafts from the Pipeline to add them here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-800">
                  {emailQueue.map((item) => (
                    <div 
                      key={item.id} 
                      className="p-4 hover:bg-gray-900/50 transition-colors cursor-pointer"
                      onClick={() => handleEditQueueItem(item)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-white truncate">
                              {item.company?.name || item.lead?.company?.name || 'Unknown Company'}
                            </span>
                            {item.lead?.company?.priority && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                item.lead.company.priority === 'A' ? 'bg-red-500/20 text-red-400' :
                                item.lead.company.priority === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {item.lead.company.priority}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 ml-auto">Click to edit</span>
                          </div>
                          <div className="text-sm text-cyan-400 mb-1 truncate">
                            To: {item.recipient_email || 'No email set'}
                          </div>
                          <div className="text-sm text-gray-300 mb-2 font-medium">
                            Subject: {item.subject}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-2">
                            {item.body?.slice(0, 150)}...
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              Scheduled: {item.scheduled_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {item.scheduled_time}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleEditQueueItem(item)}
                            className="p-2 hover:bg-cyan-500/20 text-gray-400 hover:text-cyan-400 rounded-lg transition-colors"
                            title="Edit email"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => openSendEmailModal(item)}
                            disabled={!gmailStatus?.connected || sendingEmailId === item.id}
                            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {sendingEmailId === item.id ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send size={16} />
                                Send Now
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRemoveFromQueue(item.id)}
                            className="p-2 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg transition-colors"
                            title="Remove from queue"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Tips */}
            {gmailStatus?.connected && emailQueue.length > 0 && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-cyan-400 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h4 className="text-cyan-400 font-medium mb-1">Tips for Cold Outreach</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Best send times: Tuesday-Thursday, 9-11 AM recipient's timezone</li>
                      <li>• Keep subject lines under 50 characters for better open rates</li>
                      <li>• Follow up 3-5 days after initial outreach if no response</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        company={selectedCompany}
        onSave={handleSaveCompany}
        onDelete={handleDeleteCompany}
        mode={companyModalMode}
      />

      <LeadDetailModal
        isOpen={showLeadDetailModal}
        onClose={() => setShowLeadDetailModal(false)}
        lead={leadToView}
        onEdit={handleEditLead}
        onGenerateEmail={handleGenerateEmail}
      />

      <EmailDraftModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        lead={selectedLead}
        company={selectedLead?.company}
        onApprove={handleApproveEmail}
      />

      <CSVImportModal
        isOpen={showCSVModal}
        onClose={() => setShowCSVModal(false)}
        onImport={handleImportCSV}
      />

      <EditQueueModal
        isOpen={showEditQueueModal}
        onClose={() => setShowEditQueueModal(false)}
        queueItem={selectedQueueItem}
        onSave={handleUpdateQueueItem}
      />

      <EditLeadModal
        isOpen={showEditLeadModal}
        onClose={() => setShowEditLeadModal(false)}
        lead={selectedLead}
        onSave={handleUpdateLead}
        onDelete={handleDeleteLead}
      />

      <LeadFinderModal
        isOpen={showLeadFinderModal}
        onClose={() => setShowLeadFinderModal(false)}
        onLeadsFound={() => {
          fetchCompanies();
          fetchDashboard();
        }}
      />

      {/* TheOrg Lead Finder Modal */}
      <TheOrgLeadFinderModal
        isOpen={showTheOrgModal}
        onClose={() => setShowTheOrgModal(false)}
        theOrgSearch={theOrgSearch}
        setTheOrgSearch={setTheOrgSearch}
        theOrgIndustry={theOrgIndustry}
        setTheOrgIndustry={setTheOrgIndustry}
        theOrgResults={theOrgResults}
        theOrgLoading={theOrgLoading}
        searchTheOrg={searchTheOrg}
        theOrgSelectedCompany={theOrgSelectedCompany}
        setTheOrgSelectedCompany={setTheOrgSelectedCompany}
        theOrgOrgChart={theOrgOrgChart}
        theOrgOrgChartLoading={theOrgOrgChartLoading}
        fetchTheOrgOrgChart={fetchTheOrgOrgChart}
        importTheOrgLead={importTheOrgLead}
        importingLead={importingLead}
        theOrgCredits={theOrgCredits}
        fetchTheOrgCredits={fetchTheOrgCredits}
        theOrgExecutiveSearch={theOrgExecutiveSearch}
        setTheOrgExecutiveSearch={setTheOrgExecutiveSearch}
        enrichTheOrgContact={enrichTheOrgContact}
        enrichingContact={enrichingContact}
        enrichedContacts={enrichedContacts}
      />

      {/* Send Email Modal */}
      {showSendEmailModal && emailToSend && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSendEmailModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-cyan-500/20 rounded-lg">
                    <Send className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Send Email</h3>
                    <p className="text-sm text-gray-400">Review and send your email</p>
                  </div>
                </div>
                <button onClick={() => setShowSendEmailModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                  <X className="text-gray-400" size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              {/* From Account */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From</label>
                <select
                  value={selectedFromAccount}
                  onChange={(e) => setSelectedFromAccount(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                >
                  {gmailAccounts.filter(a => a.connected).map(account => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.email} ({account.name})
                    </option>
                  ))}
                </select>
              </div>

              {/* To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To</label>
                <div className="flex items-center gap-3 px-4 py-3 bg-black border border-gray-600 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {emailToSend.recipient_name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                  </div>
                  <div>
                    <div className="text-white text-sm">{emailToSend.recipient_name}</div>
                    <div className="text-gray-400 text-xs">{emailToSend.recipient_email}</div>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  placeholder="Email subject..."
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                <textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none resize-none"
                  placeholder="Email body..."
                />
              </div>

              {/* Schedule Options */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">When to send</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSendSchedule('now')}
                    className={`flex-1 px-4 py-3 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                      sendSchedule === 'now' 
                        ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400' 
                        : 'bg-black border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <Send size={16} />
                    Send Now
                  </button>
                  <button
                    onClick={() => setSendSchedule('later')}
                    className={`flex-1 px-4 py-3 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                      sendSchedule === 'later' 
                        ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                        : 'bg-black border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <Clock size={16} />
                    Schedule
                  </button>
                </div>
              </div>

              {/* Schedule Time Picker */}
              {sendSchedule === 'later' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Schedule for</label>
                  <input
                    type="datetime-local"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Best times are Tuesday-Thursday, 9-11 AM in recipient's timezone
                  </p>
                </div>
              )}

              {/* Company Info */}
              {emailToSend.company && (
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                      {emailToSend.company.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{emailToSend.company.name}</div>
                      <div className="text-gray-400 text-xs">{emailToSend.company.industry}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowSendEmailModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmailId === emailToSend.id || (sendSchedule === 'later' && !scheduledTime)}
                className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {sendingEmailId === emailToSend.id ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : sendSchedule === 'later' ? (
                  <>
                    <Clock size={18} />
                    Schedule Email
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltimateLeadGeneration;
